import { Spin } from 'antd'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import {
    TbBuilding,
    TbChevronRight,
    TbClock,
    TbFolder,
    TbFolderCog,
    TbSitemap,
    TbTrash,
    TbUser,
    TbUserShare,
    TbUsersGroup,
} from 'react-icons/tb'

import { CheckDiskAdminAccessTask, diskAdminOverviewUrl } from '@/Containers/DiskSection/DiskAdminContainer/Hooks'
import { classNames } from '@/Ship/Helpers/classNames'

import { storageTitle } from '../Helpers/diskFormat'
import { diskFolderUrl, diskRecentUrl, diskSharedUrl, diskTrashUrl } from '../Helpers/diskRoutes'
import { DISK_SHARED_TITLE, diskBrowserStore as store } from '../Stores/DiskBrowserStore'
import { DiskQuotaCard } from './DiskQuotaCard'
import { useDiskActions } from './useDiskActions'

import type { DiskNode, DiskStorage, DiskStorageType } from '../Contracts/DiskNode'
import type { DragEvent, ReactNode } from 'react'

import styles from './Disk.module.css'

const STORAGE_ICON: Record<DiskStorageType, ReactNode> = {
    personal: <TbUser />,
    company: <TbBuilding />,
    department: <TbSitemap />,
    custom: <TbUsersGroup />,
}

interface TreeRowProps {
    /** Uuid папки; `null` — пункт без дерева («Нещодавні», «Кошик»). */
    folderUuid: string | null
    label: string
    icon: ReactNode
    nested: boolean
    active: boolean
    onOpen: () => void
    children?: ReactNode
}

/** Рядок панелі: шеврон (якщо є гілка), іконка, назва; папка приймає перетягнуті рядки. */
const TreeRow = observer(({ folderUuid, label, icon, nested, active, onOpen, children }: TreeRowProps) => {
    const actions = useDiskActions()
    const expanded = folderUuid !== null && store.tree.isExpanded(folderUuid)
    const loaded = folderUuid !== null ? store.tree.childrenOf(folderUuid) : undefined
    // Гілку показуємо, доки не з'ясувалося, що папок усередині немає
    const hasBranch = folderUuid !== null && (loaded === undefined || loaded.length > 0)
    const isDrop = folderUuid !== null && store.dropTarget === folderUuid

    const dropProps =
        folderUuid === null
            ? {}
            : {
                  onDragOver: (event: DragEvent) => {
                      if (!store.canDropOn(folderUuid)) return
                      event.preventDefault()
                      event.dataTransfer.dropEffect = 'move'
                      store.setDropTarget(folderUuid)
                  },
                  onDragLeave: () => store.setDropTarget(null),
                  onDrop: (event: DragEvent) => {
                      event.preventDefault()
                      void actions.drop(folderUuid)
                  },
              }

    return (
        <li>
            <div
                className={classNames(
                    styles['treeRow'],
                    nested && styles['treeRowNested'],
                    active && styles['treeRowActive'],
                    isDrop && styles['treeRowDrop']
                )}
                {...dropProps}
            >
                {hasBranch ? (
                    <button
                        type="button"
                        className={classNames(styles['chevron'], expanded && styles['chevronOpen'])}
                        aria-label={expanded ? `Згорнути «${label}»` : `Розгорнути «${label}»`}
                        aria-expanded={expanded}
                        onClick={() => store.tree.toggle(folderUuid)}
                    >
                        {folderUuid !== null && store.tree.loading.has(folderUuid) && expanded ? (
                            <Spin size="small" />
                        ) : (
                            <TbChevronRight />
                        )}
                    </button>
                ) : (
                    <span className={classNames(styles['chevronGap'])} />
                )}
                <button
                    type="button"
                    className={classNames(styles['treeLabel'])}
                    onClick={onOpen}
                    aria-current={active ? 'page' : undefined}
                >
                    <span className={classNames(styles['treeIcon'])}>{icon}</span>
                    <span className={classNames(styles['treeText'])} title={label}>
                        {label}
                    </span>
                </button>
            </div>
            {expanded && children}
        </li>
    )
})

const FolderBranch = observer(({ storage, parent }: { storage: DiskStorage; parent: string }) => {
    const navigate = useNavigate()
    const folders: DiskNode[] = store.tree.childrenOf(parent) ?? []

    if (folders.length === 0) return null

    return (
        <ul className={classNames(styles['treeList'])}>
            {folders.map((folder) => (
                <TreeRow
                    key={folder.uuid}
                    folderUuid={folder.uuid}
                    label={folder.name}
                    icon={<TbFolder />}
                    nested
                    active={store.location?.kind === 'folder' && store.folderUuid === folder.uuid}
                    onOpen={() => navigate(diskFolderUrl(storage.uuid, folder.uuid))}
                >
                    <FolderBranch storage={storage} parent={folder.uuid} />
                </TreeRow>
            ))}
        </ul>
    )
})

const StorageRoot = observer(({ storage }: { storage: DiskStorage }) => {
    const navigate = useNavigate()
    const location = store.location
    const active = location?.kind === 'folder' && location.storageUuid === storage.uuid && location.folderUuid === null

    return (
        <TreeRow
            folderUuid={storage.rootFolderUuid}
            label={storageTitle(storage)}
            icon={STORAGE_ICON[storage.type]}
            nested={false}
            active={active}
            onOpen={() => navigate(diskFolderUrl(storage.uuid))}
        >
            <FolderBranch storage={storage} parent={storage.rootFolderUuid} />
        </TreeRow>
    )
})

/**
 * Ліва панель: розділи диска з деревом папок і картка квоти.
 *
 * Порядок — як у референсі: швидкий доступ («Нещодавні»), далі простори
 * («Мій диск», «Спільні зі мною», «Компанія», відділи, спільні простори),
 * унизу «Кошик» (`DISK-005` §3.6.5: «Спільні зі мною» — між «Моїм диском» і
 * «Компанією», як у Google Drive й OneDrive). «Кошик» — поточного простору;
 * зі «Спільних зі мною» — «Мого диска»: кошика чужого простору людина, що має
 * лише доступ, не бачить (і бек його не віддасть). «Обрані» не
 * показуємо: зірочка «в обране» в цьому ядрі живе лише в картці ресурсу
 * (`FavoriteButton`), а ресурсу вузла диска немає.
 */
export const DiskSidebar = observer(() => {
    const navigate = useNavigate()
    const location = store.location
    const go = (url: string) => navigate(url)
    const trashStorage = store.trashStorage

    return (
        <nav className={classNames(styles['nav'])} aria-label="Розділи диска">
            <ul className={classNames(styles['treeList'])}>
                <TreeRow
                    folderUuid={null}
                    label="Нещодавні"
                    icon={<TbClock />}
                    nested={false}
                    active={location?.kind === 'recent'}
                    onOpen={() => go(diskRecentUrl())}
                />
                {store.personalStorage && <StorageRoot storage={store.personalStorage} />}
                <TreeRow
                    folderUuid={null}
                    label={DISK_SHARED_TITLE}
                    icon={<TbUserShare />}
                    nested={false}
                    active={location?.kind === 'shared'}
                    onOpen={() => go(diskSharedUrl())}
                />
                {store.companyStorage && <StorageRoot storage={store.companyStorage} />}
            </ul>

            <div className={classNames(styles['navGroup'])}>Відділи</div>
            {store.departmentStorages.length > 0 ? (
                <ul className={classNames(styles['treeList'])}>
                    {store.departmentStorages.map((storage) => (
                        <StorageRoot key={storage.uuid} storage={storage} />
                    ))}
                </ul>
            ) : (
                <div className={classNames(styles['navEmpty'])}>Ви не входите до жодного відділу</div>
            )}

            <div className={classNames(styles['navGroup'])}>Спільні простори</div>
            {store.customStorages.length > 0 ? (
                <ul className={classNames(styles['treeList'])}>
                    {store.customStorages.map((storage) => (
                        <StorageRoot key={storage.uuid} storage={storage} />
                    ))}
                </ul>
            ) : (
                <div className={classNames(styles['navEmpty'])}>Спільних просторів поки немає</div>
            )}

            {trashStorage && (
                <ul className={classNames(styles['treeList'])} style={{ marginTop: 8 }}>
                    <TreeRow
                        folderUuid={null}
                        label="Кошик"
                        icon={<TbTrash />}
                        nested={false}
                        active={location?.kind === 'trash'}
                        onOpen={() => go(diskTrashUrl(trashStorage.uuid))}
                    />
                </ul>
            )}
        </nav>
    )
})

/**
 * Вхід в адмін-панель під карткою квоти (`DISK-006` §5.1) — лише з правом
 * `DiskAdmin`, тим самим, що ховає пункт меню.
 */
const DiskAdminLink = observer(() => {
    const navigate = useNavigate()

    if (!CheckDiskAdminAccessTask()) return null

    return (
        <a
            href={diskAdminOverviewUrl()}
            className={classNames(styles['adminLink'])}
            onClick={(event) => {
                event.preventDefault()
                navigate(diskAdminOverviewUrl())
            }}
            data-testid="disk-admin-link"
        >
            <TbFolderCog aria-hidden /> Адміністрування диска
        </a>
    )
})

/** Панель з карткою квоти — для широкого екрана (ліворуч) і мобільної шухляди. */
export const DiskSidePanel = () => (
    <>
        <DiskSidebar />
        <DiskQuotaCard />
        <DiskAdminLink />
    </>
)
